import type { DividendPerYear } from "@bn/api/apiv1";

interface Kpis {
  latestYear?: number;
  latestValue: number;
  latestYearlyChange: number | null;
  count: number;
  average: number;
  sum: number;
}

const byYear = (a: DividendPerYear, b: DividendPerYear) =>
  (a.year ?? 0) - (b.year ?? 0);

export const computeDividendKpis = (
  dividends?: DividendPerYear[]
): Kpis | null => {
  if (!dividends?.length) return null;

  const sorted = [...dividends].sort(byYear);
  const actuals = sorted.filter((d) => !d.isEstimate);
  const latest = actuals.at(-1) ?? sorted.at(-1)!;
  const latestIndex = sorted.indexOf(latest);
  const previous = latestIndex > 0 ? sorted[latestIndex - 1] : undefined;

  const latestValue = latest.totalDividendPerShare ?? 0;
  const previousValue = previous?.totalDividendPerShare ?? 0;

  const latestYearlyChange =
    previousValue !== 0 ? (latestValue - previousValue) / previousValue : null;

  const sum = actuals.reduce(
    (acc, d) => acc + (d.totalDividendPerShare ?? 0),
    0
  );
  const average = actuals.length > 0 ? sum / actuals.length : 0;

  return {
    latestYear: latest.year,
    latestValue,
    latestYearlyChange,
    count: actuals.length,
    average,
    sum,
  };
};

export const buildDividendChartData = (
  dividends: DividendPerYear[] | undefined,
  solidColor: string,
  estimateColor: string
) => {
  if (!dividends?.length) return { categories: [], data: [] };
  const sorted = [...dividends].sort(byYear);
  return {
    categories: sorted.map((d) => `${d.year}${d.isEstimate ? "(e)" : ""}`),
    data: sorted.map((d) => ({
      y: d.totalDividendPerShare ?? null,
      color: d.isEstimate ? estimateColor : solidColor,
    })),
  };
};
